"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAgGridPage = exports.getRowTotals = exports.getRow = void 0;
var tslib_1 = require("tslib");
var constants_1 = require("../base/constants");
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var tableDescriptorTypes_1 = require("../structure/tableDescriptorTypes");
var dataSourceUtils_1 = require("./dataSourceUtils");
var fill_1 = tslib_1.__importDefault(require("lodash/fill"));
var findIndex_1 = tslib_1.__importDefault(require("lodash/findIndex"));
function getSubtotalLabelCellIndex(resultHeaderItems, rowIndex) {
    return findIndex_1.default(resultHeaderItems, function (headerItem) { return sdk_backend_spi_1.isResultTotalHeader(headerItem[rowIndex]); });
}
function getMinimalRowData(dv) {
    var data = dv.rawData().twoDimData();
    var rowHeaders = dv.meta().allHeaders()[0];
    var numberOfRowHeaderItems = (rowHeaders[0] || []).length;
    return data.length > 0
        ? data
        : // if there are no measures only attributes
            // create array of [null] of length equal to the number of row dimension headerItems
            fill_1.default(Array(numberOfRowHeaderItems), [null]);
}
function getCell(rowHeaderData, rowIndex, rowHeader, rowHeaderIndex, intl) {
    var rowHeaderDataItem = rowHeaderData[rowHeaderIndex][rowIndex];
    var cell = {
        field: rowHeader.id,
        rowHeaderDataItem: rowHeaderDataItem,
        isSubtotal: false,
    };
    if (sdk_backend_spi_1.isResultAttributeHeader(rowHeaderDataItem)) {
        return tslib_1.__assign(tslib_1.__assign({}, cell), { value: rowHeaderDataItem.attributeHeaderItem.name });
    }
    if (sdk_backend_spi_1.isResultTotalHeader(rowHeaderDataItem)) {
        var totalName = rowHeaderDataItem.totalHeaderItem.name;
        return tslib_1.__assign(tslib_1.__assign({}, cell), { isSubtotal: true, value: getSubtotalLabelCellIndex(rowHeaderData, rowIndex) === rowHeaderIndex
                ? intl.formatMessage({ id: "visualizations.totals.dropdown.title." + totalName })
                : null });
    }
    ts_invariant_1.default(rowHeaderDataItem, "row header is not of type IResultAttributeHeaderItem or IResultTotalHeaderItem");
}
function getRow(tableDescriptor, cellData, rowIndex, rowHeaderData, subtotalStyles, intl) {
    var row = {
        headerItemMap: {},
    };
    tableDescriptor.headers.sliceCols.forEach(function (rowHeader, rowHeaderIndex) {
        var _a;
        var _b = getCell(rowHeaderData, rowIndex, rowHeader, rowHeaderIndex, intl), isSubtotal = _b.isSubtotal, field = _b.field, value = _b.value, rowHeaderDataItem = _b.rowHeaderDataItem;
        if (isSubtotal) {
            row.type = constants_1.ROW_SUBTOTAL;
            if (!row.subtotalStyle) {
                row.subtotalStyle = (_a = subtotalStyles[rowHeaderIndex]) !== null && _a !== void 0 ? _a : undefined;
            }
        }
        row[field] = value;
        row.headerItemMap[field] = rowHeaderDataItem;
    });
    if (!tableDescriptor.hasDataLeafCols()) {
        // table has no leaf columns - it is a row-only table listing a bunch of
        // attribute element values. no point in going further as there is no additional
        // data to show
        return row;
    }
    cellData.forEach(function (cell, cellIndex) {
        var colId = tableDescriptor.headers.leafDataCols[cellIndex].id;
        ts_invariant_1.default(colId);
        row[colId] = cell;
    });
    return row;
}
exports.getRow = getRow;
function getRowTotals(tableDescriptor, dv, intl) {
    if (!dv.rawData().hasTotals()) {
        return null;
    }
    var totals = dv.rawData().totals();
    ts_invariant_1.default(totals && totals.length > 0);
    var colGrandTotals = totals[0];
    var colGrandTotalDefs = dv.definition.dimensions[0].totals;
    ts_invariant_1.default(colGrandTotalDefs);
    var grandTotalColDescriptor = tableDescriptor.getGrandTotalCol();
    var grandTotalAttrDescriptor = grandTotalColDescriptor.attributeDescriptor;
    var leafColumns = tableDescriptor.zippedLeaves;
    return colGrandTotals.map(function (totalsPerLeafColumn, totalIdx) {
        var _a;
        var grandTotalName = grandTotalColDescriptor.effectiveTotals[totalIdx].totalHeaderItem.name;
        var measureCells = {};
        var calculatedForColumns = [];
        var calculatedForMeasures = colGrandTotalDefs
            .filter(function (total) {
            return (grandTotalName === total.type &&
                total.attributeIdentifier === grandTotalAttrDescriptor.attributeHeader.localIdentifier);
        })
            .map(function (total) { return total.measureIdentifier; });
        totalsPerLeafColumn.forEach(function (value, idx) {
            var leafDescriptor = leafColumns[idx][0];
            // if code bombs here then there must be something wrong in the table / datasource code because
            // totals cannot (by definition) appear in a table that does not have measures - yet here we are
            // processing totals
            ts_invariant_1.default(tableDescriptorTypes_1.isSeriesCol(leafDescriptor));
            measureCells[leafDescriptor.id] = value;
            if (calculatedForMeasures.indexOf(leafDescriptor.seriesDescriptor.measureDescriptor.measureHeaderItem.localIdentifier) > -1) {
                calculatedForColumns.push(leafDescriptor.id);
            }
        });
        return tslib_1.__assign(tslib_1.__assign({ colSpan: {
                count: tableDescriptor.sliceColCount(),
                headerKey: grandTotalColDescriptor.id,
            } }, measureCells), (_a = {}, _a[grandTotalColDescriptor.id] = intl.formatMessage({
            id: "visualizations.totals.dropdown.title." + grandTotalName,
        }), _a.calculatedForColumns = calculatedForColumns, _a.type = constants_1.ROW_TOTAL, _a));
    });
}
exports.getRowTotals = getRowTotals;
/**
 * Given data view with a page of data and a table descriptor, this factory function creates page for consumption
 * by ag-grid.
 *
 * @param dv - data view with data to process (OK if its empty and has no data)
 * @param tableDescriptor - table descriptor
 * @param intl - intl bundle to get localized subtotal names
 */
function createAgGridPage(dv, tableDescriptor, intl) {
    var headerItems = dv.meta().allHeaders();
    var dimensions = dv.definition.dimensions;
    var minimalRowData = getMinimalRowData(dv);
    var subtotalStyles = dataSourceUtils_1.getSubtotalStyles(dimensions === null || dimensions === void 0 ? void 0 : dimensions[0]);
    var rowData = minimalRowData.map(function (dataRow, dataRowIndex) {
        return getRow(tableDescriptor, dataRow, dataRowIndex, headerItems[0], subtotalStyles, intl);
    });
    var rowTotals = getRowTotals(tableDescriptor, dv, intl);
    return {
        rowData: rowData,
        rowTotals: rowTotals,
    };
}
exports.createAgGridPage = createAgGridPage;
//# sourceMappingURL=rowFactory.js.map